import React from 'react';
import Joi from 'joi';
import moment, { isMoment } from 'moment';
import _ from 'lodash';
import Big from 'big.js';
import { documentApi } from 'api/main';
import {
    COMPANY_DOCUMENTS,
    investorErrors,
    RoleNames,
    stepIDs,
    stepStatus,
    environments,
    subscriptionStatus,
    exceptionTierOptions,
    docTypes,
    EURO_SYMBOL,
    roleCategories,
    ribErrors,
    alertType,
    entityKey,
    fundTypes,
    paymentMethods,
    COMPANY_AUDIT_DOCUMENTS_SUBTYPES,
    directDebitAllowedCountries,
} from './constants';
import { texts } from './languages/fr';
import { TUNNEL_STEPS, billStatus as capitalAccountStatus } from 'utils/constants';

import { countries } from './formData';
import { Typography } from '@mui/material';
import { routeMaps } from 'routes/routeMappings';

const joiValidationOptions = {
    abortEarly: false,
    stripUnknown: true,
    allowUnknown: true,
};

/*
    @schema must be an object that contains Joi schemas or a Joi schema
*/
export const validateSchema = (schema, value, opt = {}) => {
    let newErrors = {};
    const { error } = Joi.isSchema(schema)
        ? schema.validate(value, { ...joiValidationOptions, ...opt })
        : Joi.object(schema).validate(value, { ...joiValidationOptions, ...opt });

    if (error) {
        for (let item of error.details) newErrors[item.path] = item.message;
    }

    let objectErrors = newErrors[''] ? newErrors[''] : newErrors;

    return {
        valid: !error,
        newErrors: typeof value === 'object' ? objectErrors : newErrors,
    };
};

export const routesFilter = currentRole => route =>
    route.role.includes(currentRole) || route.role.length === 0;

export const getUserIdFromIri = iri => (iri ? +iri?.replace('/api/users/', '') : '');
export const getShareSubcategoryIdFromIRI = iri => +iri?.replace('/api/share_subcategories/', '');
export const getShareCategoryIdFromIRI = iri => +iri?.replace('/api/share_categories/', '');
export const getEntityIdFromIRI = (iri, key) => +iri?.replace(`/api/${key}/`, '');

export const dateValidator = date => {
    const compareDate = moment(date, 'YYYY-MM-DD');
    const startDate = moment().subtract(100, 'y');
    const endDate = moment().subtract(18, 'y');
    return compareDate.isBetween(startDate, endDate, 'days', '[]');
};

export const formatIBAN = value => {
    if (!value) {
        return '';
    }

    return value
        .toUpperCase()
        .replace(/\s/g, '') // remove whitespaces
        .replace(/(.{4})/g, '$1 ') // divide in groups of 4
        .trim();
};

export const canOperateWithSEPA = bankAccountNumber => {
    if (!bankAccountNumber || bankAccountNumber.length < 15) {
        return '';
    }
    return directDebitAllowedCountries.includes(bankAccountNumber.slice(0, 2).toUpperCase());
};

export const formatIBANOnType = element => {
    const countSpaces = text => {
        const spaces = text.match(/(\s+)/g);
        return spaces ? spaces.length : 0;
    };

    const position = element.selectionEnd;
    const previousValue = element.value;
    element.value = formatIBAN(element.value);

    if (position !== element.value.length) {
        const beforeCaret = previousValue.substr(0, position);
        const countPrevious = countSpaces(beforeCaret);
        const countCurrent = countSpaces(formatIBAN(beforeCaret));
        element.selectionEnd = position + (countCurrent - countPrevious);
    }

    return element.value;
};

export const inIframe = () => {
    try {
        return window.self !== window.top;
    } catch (e) {
        return true;
    }
};

export const getQueryParameter = (search, param) => {
    const query = new URLSearchParams(search);
    return !isNaN(parseInt(query.get(param))) ? parseInt(query.get(param)) : null;
};

export const getQueryParameterString = (search, param) => {
    const query = new URLSearchParams(search);
    return query.get(param);
};

export const loadScript = (src, target, id) =>
    new Promise(resolve => {
        if (!target) {
            return;
        }

        const script = document.createElement('script');
        script.setAttribute('async', false);
        script.setAttribute('id', id);
        script.setAttribute('src', src);
        target.appendChild(script);
        script.addEventListener('load', resolve, { once: true });
    });

// matches is an array of indexes corresponding to parts of the text that matched google places autocomplete input
// matches = [[startIndex0, endIndex0], [startIndex1, endIndex1], ...]
export const parseMatches = (text, matches) => {
    let result = [];

    if (matches.length === 0) {
        // if there are no matches, all text is not highlighted
        result.push({
            text: text,
            highlight: false,
        });
    } else if (matches[0][0] > 0) {
        // if the first match does not start at the beginning of the text
        // [0, startIndex0] is not highlighted
        result.push({
            text: text.slice(0, matches[0][0]),
            highlight: false,
        });
    }

    matches.forEach((match, i) => {
        let startIndex = match[0];
        let endIndex = match[1];

        result.push({
            text: text.slice(startIndex, endIndex),
            highlight: true,
        });

        if (i === matches.length - 1) {
            if (endIndex < text.length) {
                // if the last match ends before the text ending
                // [endIndex_lastMatch, endText] is not highlighted
                result.push({
                    text: text.slice(endIndex, text.length),
                    highlight: false,
                });
            }
        } else if (endIndex < matches[i + 1][0]) {
            // if there is a gap between two consecutive matches
            // [endIndex_i, startIndex_i+1] is not highlighted
            result.push({
                text: text.slice(endIndex, matches[i + 1][0]),
                highlight: false,
            });
        }
    });

    return result;
};

// the accepted file types are for the Attestation sur l'honneur
export const checkAcceptedFileTypes = (file, successCallback, failCallback) => {
    if (file && file.type !== '') {
        switch (file.type) {
            case 'application/pdf':
            case 'image/jpeg':
            case 'image/png':
            case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
                successCallback?.();
                break;
            default:
                failCallback?.();
                break;
        }
    } else {
        // if type is empty string use file extension to check type
        const extension = file?.name ? file.name.substring(file.name.lastIndexOf('.') + 1) : '';
        switch (extension) {
            case 'jpg':
            case 'jpeg':
            case 'png':
            case 'pdf':
            case 'docx':
                successCallback?.();
                break;
            default:
                failCallback?.();
                break;
        }
    }
};

// validates only DOC(X) and PDF files for document identity uploads
export const checkDocumentFileTypes = (file, successCallback, failCallback) => {
    if (file && file.type !== '') {
        switch (file.type) {
            case 'application/pdf':
            case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
            case 'application/msword': // for .doc files
                successCallback?.();
                break;
            default:
                failCallback?.();
                break;
        }
    } else {
        // if type is empty string use file extension to check type
        const extension = file?.name
            ? file.name.substring(file.name.lastIndexOf('.') + 1).toLowerCase()
            : '';
        switch (extension) {
            case 'pdf':
            case 'docx':
            case 'doc':
                successCallback?.();
                break;
            default:
                failCallback?.();
                break;
        }
    }
};

export const validateFileSize = file => file?.size <= 10 * 1024 * 1024; // max 10mb

export const checkImageFile = (file, successCallback, errorCallback) => {
    if (file && file.type !== '') {
        switch (file.type) {
            case 'image/jpeg':
            case 'image/gif':
            case 'image/png':
                successCallback?.();
                return;
            default:
                break;
        }
        errorCallback?.();
    } else {
        // if type is empty string use file extension to check type
        const extension = file?.name ? file.name.substring(file.name.lastIndexOf('.') + 1) : '';
        switch (extension) {
            case 'jpg':
            case 'jpeg':
            case 'gif':
            case 'png':
                successCallback?.();
                return;
            default:
                break;
        }
        errorCallback?.();
    }
};

// returns true when file is as PDF document
export const checkPDF = file => {
    if (file && file.type !== '') {
        return file.type === 'application/pdf';
    } else {
        const extension = file?.name ? file.name.substring(file.name.lastIndexOf('.') + 1) : '';
        return extension === 'pdf';
    }
};

export const getFileExtension = file => {
    if (file && file.type !== '') {
        switch (file.type) {
            case 'application/pdf':
                return 'pdf';
            case 'image/jpeg':
                return 'jpeg';
            case 'image/png':
                return 'png';
            case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
                return 'docx';
            case 'application/vnd.ms-excel':
                return 'xls';
            case 'text/csv':
                return 'csv';
            case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
                return 'xlsx';
            case 'application/x-zip-compressed':
            case 'application/zip':
                return 'zip';
            default:
                break;
        }
    } else if (file && file.type === '') {
        // if type is empty string use file extension to check type
        const extension = file?.name ? file.name.substring(file.name.lastIndexOf('.') + 1) : '';
        switch (extension) {
            case 'docx':
            case 'jpeg':
            case 'png':
            case 'jpg':
            case 'pdf':
                return extension;
            default:
                return '';
        }
    } else return '';
};

export const parseDate = isoDate => {
    const timestamp = Date.parse(isoDate);
    if (!isNaN(timestamp)) {
        const date = new Date(isoDate);
        return date.toLocaleDateString('fr-FR');
    }
    return null;
};

export const arrayLodashCustomizer = (objValue, srcValue, key) => {
    if (_.isArray(srcValue) && key !== 'subscriptions') {
        return [...srcValue];
    } else if (key === 'subscriptions' && srcValue?.length > objValue?.length) {
        // a subscription was deleted
        return [...objValue];
    }
    if (objValue !== null && typeof objValue === 'object' && typeof srcValue !== 'object')
        return objValue;
    return srcValue === null || typeof srcValue === 'undefined' ? objValue : undefined;
};

export const parseNumber = (number, decimals = 2, alwaysShowDecimals = false) => {
    if (isNaN(number) || !number) return '0';
    let hasDecimals = alwaysShowDecimals ? true : Math.round(number) !== number;
    const result = parseFloat(number).toLocaleString('fr', {
        minimumFractionDigits: hasDecimals ? decimals : 0,
        maximumFractionDigits: hasDecimals ? decimals : 0,
    });
    return result;
};

export const parseSum = sum => {
    return parseNumber(sum) + EURO_SYMBOL;
};

export const parseInvestmentFundName = fund => {
    if (fund) {
        const { type, name } = fund;
        const nbsp = String.fromCharCode(160);
        const formattedName = name.split(' ').join(nbsp);
        const formattedType = type.split(' ').join(nbsp);
        return `${formattedType}${nbsp}-${nbsp}${formattedName}`;
    }
    return '';
};

export const checkFormDataHasFiles = formData =>
    formData.entries().next().value.includes('files[]');

export const initJsonField = (json, defaultValue, otherValue) => {
    let field = '';
    let otherField = '';
    try {
        const obj = JSON.parse(json, {});
        field = obj.choice || defaultValue;
        otherField = obj.other || '';
        return [field, otherField];
    } catch {
        // json is invalid
        if (!json) {
            field = defaultValue;
        } else {
            field = otherValue;
            otherField = json || '';
        }
        return [field, otherField];
    }
};

export const saveInStorage = (key, value) => {
    if (typeof window === 'undefined') {
        return '';
    }

    return window.localStorage.setItem(key, JSON.stringify(value));
};

export const getFromStorage = key => {
    if (typeof window === 'undefined') {
        return '';
    }

    const item = window.localStorage.getItem(key);
    return jsonParseSafe(item, '');
};

export const deleteFromStorage = key => {
    if (typeof window === 'undefined') {
        return '';
    }

    return window.localStorage.removeItem(key);
};

export const ucFirst = payload => {
    if (!payload) return '';
    let value = payload
        .split(' ')
        .map(str => str.charAt(0).toUpperCase() + str.substring(1).toLowerCase())
        .join(' ');

    if (value.includes('-')) {
        value = value
            .split('-')
            .map(str => {
                if (str.charAt(0) !== str.charAt(0).toUpperCase()) {
                    return str.charAt(0).toUpperCase() + str.substring(1).toLowerCase();
                }
                return str;
            })
            .join('-');
    }

    return value;
};

export const updateArrayEntryById = (array, entry, idKey = 'id') => {
    const index = array.findIndex(x => x[idKey] === entry[idKey]);
    return [...array.slice(0, index), entry, ...array.slice(index + 1)];
};

export const parsePercentage = p => parseFloat(p.slice(0, p.indexOf('.') + 3));

export const createDocumentsIRI = id => (id ? `/api/documents/${id}` : null);

export const jsonParseSafe = (value, defaultValue = null) => {
    try {
        if (value === null) return defaultValue;
        return JSON.parse(value);
    } catch (error) {
        return defaultValue;
    }
};

export const getCompanyDocuments = (company, documentList) => {
    const documents = [];

    for (let key in company) {
        if (COMPANY_DOCUMENTS.includes(key) && company[key]) {
            if (
                (key !== 'legalRepresentativeDocument' &&
                    key !== 'legalRepresentativeIdentityDocument' &&
                    key !== 'beneficiariesIdentityDocuments' &&
                    key !== 'legalRepresentativesIdentityDocuments') ||
                (key === 'legalRepresentativeDocument' && !company.legalRepresentant) ||
                (key === 'legalRepresentativeIdentityDocument' && !company.legalRepresentant)
            ) {
                const current = documentList.find(x => x.id === company[key].id);
                documents.push({ ...company[key], ...current, key });
            } else if (
                key === 'beneficiariesIdentityDocuments' ||
                key === 'legalRepresentativesIdentityDocuments'
            ) {
                for (let doc of company[key]) {
                    documents.push({ ...doc, key });
                }
            } else if (key === 'beneficiariesRegister') {
                documents.push({ id: getEntityIdFromIRI(company[key], entityKey.DOCUMENT) });
            }
        }
    }

    // Add User Subscription documents (ie: RIB)
    if (company.user?.subscriptions?.[0]?.subscriptionDocuments?.length) {
        company.user.subscriptions[0].subscriptionDocuments.forEach(doc => {
            if (!documentList.find(x => x.id === doc.id)) {
                documents.push(doc);
            }
        });
    }

    return documents;
};

export const getInProgressSubscription = user =>
    user?.subscriptions?.find(sub => sub.subscriptionStatus === subscriptionStatus.IN_PROGRESS);

// block navigation for distributor if step before last step is verified
export const blockDistributorNavigation = internalUser => {
    const inProgressSubscription = getInProgressSubscription(internalUser);
    const step = inProgressSubscription?.userTunnelSteps?.find(
        step => step.tunnelStep.step.id === stepIDs.DISTRIBUTOR_DOCUMENTS
    );

    return step ? step.status === stepStatus.VERIFIED : false;
};

export const parseFullName = user => {
    if (user?.fullName) return user.fullName;
    if (user) {
        return `${ucFirst(user.firstName)} ${user.lastName?.toUpperCase()}`;
    }
    return '-';
};

export const parseBackendViolations = data => {
    if (!!data?.violations) {
        const fieldsError = {};
        data.violations.forEach(e => (fieldsError[e['propertyPath']] = e.message));
        return fieldsError;
    }
    return null;
};

export const isJumioDoc = doc =>
    doc?.type === docTypes.ID ||
    doc?.type === docTypes.SECONDARY_ID ||
    doc?.type === docTypes.FACTURE;

export const getInvestorErrorMessage = errorKey => {
    switch (errorKey) {
        case investorErrors.INVALID_NIF:
            return texts.INVALID_NIF_MESSAGE;
        case investorErrors.CO_SUBSCRIBER_INVALID_NIF:
            return texts.INVALID_NIF_MESSAGE;
        case investorErrors.INVALID_IBAN:
            return texts.INVALID_IBAN_MESSAGE;
        case ribErrors.EMPTY_BIC:
            return texts.EMPTY_BIC_MESSAGE;
        case ribErrors.BIC_INVALID:
            return texts.BIC_INVALID;
        case ribErrors.DIRECT_DEBIT_NOT_ALLOWED:
            return texts.DIRECT_DEBIT_NOT_ALLOWED;
        default:
            return '';
    }
};

export const getDocumentAvailability = (tunnelSteps, stepId) => {
    const currentStepStatus = tunnelSteps?.find(ts => ts.tunnelStep.step.id === +stepId)?.status;

    return currentStepStatus === stepStatus.VERIFIED || currentStepStatus === stepStatus.FAILED;
};

export const getDocumentName = (doc, fullName, belongsToCoSubscriber) => {
    if (!doc) {
        return null;
    }

    if (fullName) {
        return fullName;
    }

    if (
        doc.type === docTypes.CAPITAL_ACCOUNT ||
        doc.key === 'beneficiariesIdentityDocuments' ||
        doc.key === 'legalRepresentativesIdentityDocuments'
    ) {
        return doc.name;
    }

    if (doc.type === docTypes.OTHER) {
        return (
            doc.comment ||
            getOtherDocumentNameBySubtype(doc.subtype) ||
            getDocumentNameByKey(doc.key) ||
            texts.AUTRE
        );
    } else if (doc.key) {
        return getDocumentNameByKey(doc.key);
    } else if (
        doc.type === docTypes.COMPANY &&
        COMPANY_AUDIT_DOCUMENTS_SUBTYPES.includes(doc.subtype)
    ) {
        return getDocumentNameByKey(doc.subtype);
    }

    if (doc.type === docTypes.CAPITAL_CALL || doc.type === docTypes.CLOSING) {
        return doc.name;
    }

    return getDocumentNameByType(doc.type, doc.subtype, belongsToCoSubscriber, doc?.fundType);
};

export const getDocumentNameByType = (type, subtype, belongsToCoSubscriber, fundType) => {
    const doctype = type === docTypes.ID || type === docTypes.SECONDARY_ID ? subtype : type;

    switch (doctype) {
        case docTypes.KBIS:
            return texts.KBIS_DOC;
        case docTypes.STATUTES:
            return texts.STATUTES_DOC;
        case docTypes.AFFIDAVIT:
            return texts.AFFIDAVIT_DOC;
        case docTypes.AUTORISATION_DE_PRELEVEMENT:
            return texts.AP_DOC;
        case docTypes.AUTORISATION_DE_PRELEVEMENT_DISABLED:
            return texts.AP_DOC_DISABLED;
        case docTypes.SUBSCRIPTION_FORM:
            return texts.BS_DOC;
        case docTypes.POUVOIR:
            return texts.POUVOIR_DOC;
        case 'PASSPORT':
            return belongsToCoSubscriber ? texts.CO_SUBSCRIBER_ID_CARD : texts.PASSPORT;
        case 'ID_CARD':
            return belongsToCoSubscriber ? texts.CO_SUBSCRIBER_ID_CARD : texts.ID_CARD;
        case docTypes.DRIVING_LICENSE:
            return texts.DRIVING_LICENSE;
        case docTypes.FACTURE:
            return texts.FACTURE_DOC;
        case docTypes.BLANCHIMENT:
            return texts.BLANCHIMENT_DOC;
        case docTypes.INVESTOR_REDUCED_VIGILANCE:
            return texts.INVESTOR_VIGILANCE_DOC;
        case docTypes.BENEFICIARY_REDUCED_VIGILANCE:
            return texts.BENEFICIARY_VIGILANCE_DOC;
        case docTypes.BENEFICIARY_DOCUMENT:
            return texts.BENEFICIARY_DOC;
        case 'FUND_REGULATION':
            return fundType === fundTypes.FPS
                ? texts.FUND_REGULATIONS_FPS_2
                : texts.FUND_REGULATIONS;
        case 'DICI':
            return texts.DICI;
        case 'RIB':
            return texts.RIB;
        case docTypes.BENEFICIARY_IDENTIFICATION:
            return texts.BENEFICIARY_IDENTIFICATION_DOC;
        case docTypes.FATCA:
            return texts.FATCA_DOC;
        case docTypes.ADMINISTRATORS_IDENTIFICATION:
            return texts.ADMINISTRATOR_IDENTIFICATION_DOC;
        case docTypes.DIRECTORS_IDENTIFICATION:
            return texts.DIRECTOR_IDENTIFICATION_DOC;
        case docTypes.ASSOCIATES_IDENTIFICATION:
            return texts.ASSOCIATE_IDENTIFICATION_DOC;
        case docTypes.OTHER:
            return texts.AUTRE;
        case docTypes.DISTRIBUTOR_BILL:
            return texts.DISTIBUTOR_INVOICE;
        case docTypes.DISTRIBUTOR_BILL_EXCEL:
            return texts.DISTIBUTOR_INVOICE_EXCEL;
        case docTypes.TAX_EXEMPTION:
            return texts.TAX_EXEMPTION_DOC;
        case docTypes.INVESTOR_CLASSIFICATION:
            return texts.INVESTOR_CLASSIFICATION_DOC;

        default:
            return type ? type[0].toUpperCase() + type.toLowerCase().slice(1) : '-';
    }
};

export const getOtherDocumentNameBySubtype = subtype => {
    switch (subtype) {
        case 'BALANCE_SHEET_TOTAL':
            return texts.ELIGIBILITY_BALANCE_SHEET_TOTAL_DOC_LABEL;
        case 'NET_SALES_OR_REVENUES':
            return texts.ELIGIBILITY_NET_SALES_REVENUE_DOC_LABEL;
        case 'HIGHER_EQUITY':
            return texts.ELIGIBILITY_HIGHER_EQUITY_DOC_LABEL;
        case 'TRANSACTIONS_COMPLETED':
            return texts.TRANSACTIONS_COMPLETED_DOC_LABEL;
        case 'PORTFOLIO_VALUE':
            return texts.PORTFOLIO_VALUE_DOC_LABEL;
        case 'PROFESSIONAL_POSITION_HELD':
            return texts.PROFESSIONAL_POSITION_HELD_DOC_LABEL;
        case 'EXEMPTION_CONDITION_PROOF':
            return texts.EXEMPTION_CONDITION_PROOF_DOC;
        default:
            return subtype;
    }
};

export const getDocumentNameByKey = key => {
    if (!key) {
        return null;
    }
    switch (key) {
        case 'legalRepresentativeDocument':
        case 'legalRepresentantId':
            return texts.LEGAL_REPRESENTANT_AUTHORITY_ALT;
        case 'certificateORIAS':
        case 'oriasAttestation':
            return texts.ORIAS_ATTESTATION;
        case 'legalRepresentativeCriminalRecord':
        case 'criminalRecordExtract':
            return texts.CRIMINAL_RECORD_EXTRACT_ALT;
        case 'beneficiariesRegister':
            return texts.BENEFICIARIES_REGISTER;
        case 'beneficiariesIdentityDocuments':
            return texts.BENEFICIARIES_IDS;
        case 'legalRepresentativesIdentityDocuments':
            return texts.LEGAL_REPRESENTATIVES_IDS;
        case 'kbis':
            return texts.KBIS_RECENT_ALT;
        case 'insuranceCertificate':
            return texts.INSURANCE_CERTIFICATE;
        case 'legalRepresentativeIdentityDocument':
        case 'legalRepresentantAuthority':
            return texts.LEGAL_REPRESENTANT_ID_ALT;
        case 'signatoryIdentityDocument':
        case 'signatoryId':
            return texts.SIGNATORY_ID_ALT;
        case 'rib':
            return texts.RIB;
        default:
            return key[0].toUpperCase() + key.toLowerCase().slice(1);
    }
};

export const downloadClick = (url, filename, newTab = true, purge = true) => {
    if (url) {
        const link = document.createElement('a');
        if (newTab) link.target = '_blank';
        if (filename) link.download = purge ? filename.replace(/\./g, '-') : filename;
        link.href = url;
        link.click();
    }
};

export const handleDocumentDownload = (doc, setDownloadingState, setNotification) => {
    if (doc) {
        setDownloadingState(true);
        documentApi
            .downloadDocumentById(doc.id)
            .then(response => {
                const bytes = new Uint8Array(response.data);
                const blob = new Blob([bytes], { type: doc.mimeType });
                const url = window.URL.createObjectURL(blob);
                downloadClick(url);
                setDownloadingState(false);
            })
            .catch(() => {
                setNotification('FAILED_DOCUMENT_DOWNLOAD');
                setDownloadingState(false);
            });
    }
};

export const getAlertTypeForUser = subscription => {
    let isWarningAlert = false;
    for (const step of subscription?.userTunnelSteps || []) {
        for (const alert of step.alerts) {
            if (alert.type === alertType.CRITICAL && !alert.solved) {
                return alertType.CRITICAL;
            } else if (alert.type === alertType.WARNING && !alert.solved) {
                isWarningAlert = true;
            }
        }
    }

    return isWarningAlert ? alertType.WARNING : '';
};

export const extractSectionAndQuestion = key => {
    const parts = key.split('.');
    const section = parts.find(p => p.includes('group'));
    const question = parts.find(p => p.includes('question'));
    return { section, question };
};

export const parseKnowledgeLevels = knowledgeLevels => {
    const regexKnowledgeLevels = /Level_1: (?<level_1>-?\d+), Level_2: (?<level_2>-?\d+)/;
    const match = regexKnowledgeLevels.exec(knowledgeLevels);
    return [match.groups.level_1, match.groups.level_2];
};

export const parseSlpKnowledgeLevels = (slpKnowledgeLevels, level) => {
    const regexKnowledgeLevel1 = /Level_1: (?<level_1>-?\d+)/;
    const regexKnowledgeLevel2 = /Level_2: (?<level_2>-?\d+)/;
    const match =
        1 === level
            ? regexKnowledgeLevel1.exec(slpKnowledgeLevels)
            : regexKnowledgeLevel2.exec(slpKnowledgeLevels);
    return match.groups.level_1 || match.groups.level_2;
};

export const parseChoice = (choice, other, messages) => {
    if (choice !== 'general_options.other_2' && choice !== 'general_options.other') {
        return messages[choice];
    } else {
        return `Autre - ${other}`;
    }
};

export const parseInvestorLabel = (option, messages, fixedCriteria) => {
    const nbsp = String.fromCharCode(160);
    if (option === 'step34_fpci_pm_real.question_16.label') {
        const subOptionsLabels = fixedCriteria?.map(criteria => messages?.[criteria]) || [];
        const mainLabel = messages?.[option].replace('ci-dessous [+]', '');
        const secondaryLabel = subOptionsLabels.reduce((acc, curr, index) => {
            if (index === 0) {
                return acc + `${curr}`;
            }
            return acc + `, ${curr}`;
        }, '');
        return (
            <React.Fragment>
                <Typography component="span">
                    {mainLabel}
                    {nbsp}:
                </Typography>
                <Typography component="span" display="block">
                    {secondaryLabel}.
                </Typography>
            </React.Fragment>
        );
    } else if (
        option === 'step34_fpci_pm_real.question_18.option_1.label' ||
        option === 'step34_fpci_pm_real.question_18.option_2.label' ||
        option === 'step34_fpci_pm_real.question_18.option_3.label'
    ) {
        const mainLabel = messages?.['step34_fpci_pm_real.question_18.label']
            .replace('Investisseur ', '')
            .replace(' [+]', '')
            .replace('suivantes', '');
        const subOptionLabel = messages?.[option].replace(';', '');
        return (
            <React.Fragment>
                <Typography component="span">
                    {mainLabel}
                    {nbsp}:
                </Typography>
                <Typography component="span" display="block">
                    {subOptionLabel}.
                </Typography>
            </React.Fragment>
        );
    } else if (
        option === 'step34.question_2.option_1' ||
        option === 'step34.question_2.option_2' ||
        option === 'step34.question_2.option_3'
    ) {
        const mainLabel = messages?.['step34.question_2.label']
            .replace('Soit être un investisseur ', '')
            .replace('suivantes [+]', '');
        const subOptionLabel = messages?.[option].replace(' ;', '');
        return (
            <React.Fragment>
                <Typography component="span">
                    {mainLabel}
                    {nbsp}:
                </Typography>
                <Typography component="span" display="block">
                    {subOptionLabel}.
                </Typography>
            </React.Fragment>
        );
    }
    const label = messages?.[option] || '';
    return label.replace('Soit être un investisseur ', '').replace('Investisseur ', '');
};

export const boolToStr = (boolVal, trueValue = 'true', falseValue = 'false') => {
    if (typeof boolVal !== 'boolean') {
        return '';
    }
    return boolVal ? trueValue : falseValue;
};

export const convertToBoolOrNull = (val, trueValue = 'true', falseValue = 'false') => {
    if (typeof val === 'boolean') {
        return val;
    } else if (typeof val === 'string') {
        if (val === trueValue) {
            return true;
        } else if (val === falseValue) {
            return false;
        }
        return null;
    }
    return null;
};

export const convertToBoolOrUndefined = (val, trueValue = 'true', falseValue = 'false') => {
    if (typeof val === 'boolean') {
        return val;
    } else if (typeof val === 'string') {
        if (val === trueValue) {
            return true;
        } else if (val === falseValue) {
            return false;
        }
    }
};

export const getSubscriptionRoleName = currentSubscription => {
    return Object.values(RoleNames).find(value => value === currentSubscription?.role?.name);
};

export const isStatusOk = userTunnelStep => {
    const tsInfo = TUNNEL_STEPS.find(ts => ts.stepId === userTunnelStep?.tunnelStep?.step?.id);
    if (tsInfo) {
        return tsInfo.allowNext.includes(userTunnelStep.status);
    }
    return false;
};

// arrayToFill can be larger than referenceArray
export const fillArrayToMatchSize = (arrayToFill, referenceArray, fillingObject) => {
    let res = [...arrayToFill];
    if (arrayToFill.length < referenceArray.length) {
        const lengthDifference = referenceArray.length - arrayToFill.length;
        res = [...arrayToFill, ...Array.from({ length: lengthDifference }, () => fillingObject)];
    }
    return res;
};

// this method replaces last space with a unbreakable space character
export const formatLabel = label => {
    const lastSpacePos = label.lastIndexOf(' ');
    return label.substring(0, lastSpacePos) + '\xa0' + label.substring(lastSpacePos + 1);
};

export const getRenderRoleName = role => {
    if (!role) {
        return null;
    }
    switch (role.name) {
        case RoleNames.ADMIN_CIF:
            return texts.ADMIN;
        case RoleNames.COMMERCIALISATEUR:
            return texts.CONSULTANT;
        case RoleNames.NETWORK_ADMIN:
            return texts.NETWORK_ADMIN;
        case RoleNames.PROMOTER_ADMIN:
            return texts.ADMIN;
        case RoleNames.PROMOTER_STANDARD:
            return texts.STANDARD;
        case RoleNames.PROVIDER_STANDARD:
            return texts.PROVIDER_STANDARD;
        case RoleNames.PROVIDER_SIGNATORY:
            return texts.PROVIDER_SIGNATORY;
        case RoleNames.PROVIDER_ADMIN:
            return texts.PROVIDER_ADMIN;
        default:
            return role.name;
    }
};

export const parseReceivedNotifications = (notifications, withFrom) =>
    notifications?.map(notification => {
        let title = `${moment(notification.createdAt).format('L')} - ${notification.subject}`;
        if (withFrom) {
            let company = 'Peqan';
            const distributorCompany = notification.sender?.subscriptions?.[0]?.distributorCompany;
            if (distributorCompany) {
                company = distributorCompany?.corporateName;
            }
            title = `${company} - ${title}`;
        }
        return {
            id: notification.id,
            title,
            content: notification.content,
        };
    }) || [];

export const lastEligibilityQuestion = (isSLP, isFPS, message) => {
    // TRANSLATION ISSUE
    if (isSLP) {
        return message.replace('un FPCI', 'une SLP');
    }
    if (isFPS) {
        return message.replace('un FPCI', 'un FPS');
    }
    return message;
};

export const is400StatusHandled = response => {
    if (
        ((response?.config?.url?.includes('register/3') ||
            response?.config?.url?.includes('register/46')) &&
            response?.data?.errors?.[0]?.message?.includes(
                investorErrors.CONTACT_INFO_US_PERSON
            )) ||
        response?.config?.url?.includes('networks') ||
        (response?.config?.url?.includes('register/14') &&
            (response?.data?.errors?.[0]?.message?.includes(investorErrors.INVALID_IBAN) ||
                response?.data?.errors?.[0]?.message?.includes(investorErrors.INVALID_NIF)))
    ) {
        return true;
    }

    if (
        response?.config?.url?.includes('register/34') &&
        (response?.data?.errors?.[0]?.message === investorErrors.ELIGIBILITY_INVALID ||
            response?.data?.errors?.[0]?.message === investorErrors.SUM_INVALID ||
            response?.data?.errors?.[0]?.message === investorErrors.ELIGIBILITY_INITIAL_INVALID)
    ) {
        return true;
    }

    if (
        response?.config?.url?.includes('register/41') &&
        response?.data?.errors?.[0]?.message === investorErrors.SUM_INVALID
    ) {
        return true;
    }

    if (
        response?.config?.url?.includes('referrals/invite') &&
        response?.data?.detail === investorErrors.SUBSCRIPTION_IN_PROGRESS
    ) {
        return true;
    }

    if (
        response?.config?.url?.includes('subscription_alerts/') &&
        response?.config?.method === 'patch'
    ) {
        return true;
    }

    if (response?.config?.url?.includes('/subscriptions/external/servicer')) {
        return true;
    }

    if (
        response?.config?.url?.includes('investment_funds') &&
        (response?.config?.method === 'patch' || response?.config?.method === 'post')
    ) {
        return true;
    }

    return !!(
        response?.config?.url?.includes('distributor_companies') &&
        (response?.config?.method === 'patch' || response?.config?.method === 'post')
    );
};

const matchJumioStepToDoc = id => {
    switch (id) {
        case stepIDs.ID_CHECK:
            return docTypes.ID;
        case stepIDs.SECOND_ID_CHECK:
            return docTypes.SECONDARY_ID;
        case stepIDs.ADDRESS_CHECK:
            return docTypes.FACTURE;
        default:
            return null;
    }
};

export const getDocumentJumioStatus = (steps, docType, isInvestor) => {
    const excludedAlerts = [
        'id_check.additional_checks.screening_sanctions',
        'user_address_updated.affidavit_uploaded',
    ];
    const investorExcludedAlerts = ['address_check.imprecise_location'];
    const foundStep = steps?.find(step => matchJumioStepToDoc(step.tunnelStep.step.id) === docType);
    if (foundStep) {
        const alerts =
            foundStep.alerts?.filter(alert => {
                if (isInvestor) {
                    return (
                        ![...excludedAlerts, ...investorExcludedAlerts].includes(alert.message) &&
                        !alert.solved
                    );
                }
                return !excludedAlerts.includes(alert.message) && !alert.solved;
            }) || [];
        return { verified: foundStep.status === 'verified', alerts: alerts };
    }

    return null;
};

export const getTermsAndConditionsURL = () => {
    let env = '';
    switch (process.env.REACT_APP_ENV) {
        case environments.TEST:
            env = 'test';
            break;
        case environments.PREPROD:
            env = 'preprod';
            break;
        case environments.PROD:
            env = 'prod';
            break;
        default:
            env = 'dev';
    }

    return `https://ho-pu-backend-${env}.s3.eu-west-3.amazonaws.com/CGU_Plateforme_PE_Tech_102022.pdf`;
};

export const getFundShareCategories = (fundId, categories = []) =>
    categories.filter(
        c => getEntityIdFromIRI(c.investmentFund, entityKey.INVESTMENT_FUND) === fundId
    );

export const getCustomMinInvestment = (investorQualification, subcategories = []) => {
    if (exceptionTierOptions.includes(investorQualification)) return 30000;

    const min = subcategories
        .filter(sc => sc.accessibleToDistribution && sc.minInvestedAmount)
        .map(sc => sc.minInvestedAmount);

    return Math.min(...min);
};

export const getPaginationData = response => {
    const paginationMetadata = response?.headers?.['pagination-metadata'] || '';
    const paginationParts = paginationMetadata.split('/');
    const currentPage = +paginationParts?.[0] || null;
    const lastPage = +paginationParts?.[1] || null;

    return { currentPage, lastPage };
};

export const getFilenameFromHeaders = headers => {
    let headerValue = headers['content-disposition'];
    if (!headerValue) return null;

    // Split the header into parts by semicolon
    const parts = headerValue.split(';').map(p => p.trim());

    // Try to find filename* (RFC 5987)
    for (const part of parts) {
        if (part.toLowerCase().startsWith('filename*=')) {
            const match = part.split("''");
            if (match.length === 2) {
                try {
                    return decodeURIComponent(match[1]);
                } catch (e) {
                    if (process.env.REACT_APP_ENV !== environments.PROD) {
                        console.log('Filename decode failed:', e);
                    }
                    return null;
                }
            }
        }
    }

    // Fallback: look for filename=
    for (const part of parts) {
        if (part.toLowerCase().startsWith('filename=')) {
            const val = part.slice('filename='.length).trim();
            // Remove surrounding quotes if any
            if (val.startsWith('"') && val.endsWith('"')) {
                return val.slice(1, -1);
            }
            return val;
        }
    }

    return null;
};

export const deviceIsMobile = () => /Mobi|Android/i.test(navigator.userAgent);

export const exportData = (data, headers, fileType = 'application/vnd.ms-excel', newTab = true) => {
    const bytes = new Uint8Array(data);
    const filename = getFilenameFromHeaders(headers);
    let file = new File([bytes], filename, { type: fileType });
    const url = window.URL.createObjectURL(file);
    downloadClick(url, filename, newTab, false);
};

export const addBusinessDays = (originalDate, numDaysToAdd) => {
    const Sunday = 0;
    let daysRemaining = numDaysToAdd;

    const newDate = isMoment(originalDate) ? originalDate.clone() : moment(originalDate).clone();

    while (daysRemaining > 0) {
        newDate.add(1, 'days');
        if (newDate.day() !== Sunday) {
            daysRemaining--;
        }
    }

    return newDate;
};

export const subtractBusinessDays = (originalDate, numDaysToAdd) => {
    const Sunday = 0;
    let daysRemaining = numDaysToAdd;

    const newDate = isMoment(originalDate) ? originalDate.clone() : moment(originalDate).clone();

    while (daysRemaining > 0) {
        newDate.subtract(1, 'days');
        if (newDate.day() !== Sunday) {
            daysRemaining--;
        }
    }

    return newDate;
};

export const getPartialPaymentsTotal = payment => {
    const partials = jsonParseSafe(payment?.partialPayments, []);
    return partials.reduce((acc, crt) => acc.plus(crt.amount), new Big(0)).toNumber();
};

export const handleOpenDocument = url => {
    if (!url) return;
    const tab = window.open();
    tab.location.href = url;
};

export const handleDocumentPreview = (doc, setDownloadingDoc) => {
    if (!doc) {
        return;
    }
    setDownloadingDoc(doc.id);
    documentApi
        .downloadDocumentById(doc.id)
        .then(response => {
            const data = new Uint8Array(response.data);
            const blob = new Blob([data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            handleOpenDocument(url);
            setDownloadingDoc(null);
        })
        .catch(() => {
            setDownloadingDoc(null);
        });
};

export const capitaAccountSorter = (a, b) => {
    if (moment(a.date).isBefore(moment(b.date), 'day')) {
        return 1;
    } else {
        return -1;
    }
};

export const closingSorter = (a, b) => {
    if (moment(a.issueDate).isBefore(moment(b.issueDate), 'day')) {
        return 1;
    } else {
        return -1;
    }
};

export const documentSorter = (a, b) => {
    if (moment(a.createdAt).isBefore(moment(b.createdAt), 'day')) {
        return 1;
    } else {
        return -1;
    }
};

export const getLatestCapitalAccount = list => list.sort(capitaAccountSorter)?.[0];
export const getLatestClosing = list => list.sort(closingSorter)?.[0];

const getSubscriptionTotalCapitalCalled = ({
    subscription,
    capitalCalls,
    subscriptionFinishedBeforeLatestClosing,
    subscriptionFinishedBeforeLatestCapitalCall,
}) => {
    const investingSum = new Big(subscription.investmentCharacteristics.investingSum);
    const totalPercentageCalled = capitalCalls.reduce(
        (acc, crt) => acc + +crt.percentage,
        subscription.investmentFund.firstInstalmentPercentage
    );

    if (subscription.investmentFund.firstInstalmentPercentage) {
        // first installment percentage + all the capital call percentages
        return investingSum.times(totalPercentageCalled).div(100).toNumber();
    } else if (
        subscriptionFinishedBeforeLatestClosing ||
        subscriptionFinishedBeforeLatestCapitalCall
    ) {
        // chronology is:
        // 1 BS signature
        // 2 capital call / closing
        return investingSum.times(totalPercentageCalled).div(100).toNumber();
    } else {
        // chronology is:
        // 1 capital call / closing or missing
        // 2 BS signature
        return 0;
    }
};

const getSubscriptionLiquidityDate = ({
    subscription,
    latestCapitalAccount,
    subscriptionFinishedBeforeLatestClosing,
    subscriptionFinishedBeforeLatestCapitalCall,
    fund,
    subscriptionNAV,
}) => {
    // subscription must be finished (signed by Servicer) before either the latest closing OR latest capital call

    if (fund?.evergreen) {
        return subscriptionNAV?.activatedAt || null;
    } else if (subscription.investmentFund.firstInstalmentPercentage) {
        return latestCapitalAccount?.date;
    } else if (
        subscriptionFinishedBeforeLatestClosing ||
        subscriptionFinishedBeforeLatestCapitalCall
    ) {
        return latestCapitalAccount?.date;
    } else {
        return null;
    }
};

const getSubscriptionValuation = ({
    subscription,
    latestCapitalAccount,
    capitalCalls,
    closings,
    subscriptionNAV,
    subscriptionFinishedBeforeLatestCapitalCall,
    subscriptionFinishedBeforeLatestClosing,
    subcategory,
    fund,
}) => {
    const { shareValue } = subscription.investmentCharacteristics;
    const investingSum = new Big(subscription.investmentCharacteristics.investingSum);
    const sharesNumber = shareValue ? investingSum.div(shareValue) : new Big(0);

    const capitalPercentageCalledAfterLatestCapitalAccount = capitalCalls
        .filter(call =>
            latestCapitalAccount
                ? moment(latestCapitalAccount.date).isBefore(moment(call.activatedAt), 'd')
                : false
        )
        .reduce((acc, crt) => acc + +crt.percentage, 0);
    const capitalCalledAfterLatestCapitalAccount = investingSum
        .times(capitalPercentageCalledAfterLatestCapitalAccount)
        .div(100);

    const capitalCallsAfterSubscriptionSignature = capitalCalls.filter(call =>
        moment(call.activatedAt).isAfter(moment(subscription.finishedSubscriptionAt), 'd')
    );
    const closingsAfterSubscriptionSignature = closings.filter(closing =>
        moment(closing.activatedAt).isAfter(moment(subscription.finishedSubscriptionAt), 'd')
    );
    const capitalCallsBetweenSubscriptionSignatureAndCapitalAccount =
        capitalCallsAfterSubscriptionSignature.find(call =>
            latestCapitalAccount?.date
                ? moment(call.activatedAt).isBefore(moment(latestCapitalAccount.date), 'd')
                : true
        );
    const closingsBetweenSubscriptionSignatureAndCapitalAccount =
        closingsAfterSubscriptionSignature.find(closing =>
            latestCapitalAccount?.date
                ? moment(closing.activatedAt).isBefore(moment(latestCapitalAccount.date), 'd')
                : true
        );

    // evergreen funds special condition for when at least one NAV was activated
    if (fund?.evergreen) {
        return subscriptionNAV ? shareValue * subscriptionNAV.vl : 0;
    }

    if (subcategory?.immediatePayment) {
        return investingSum.toNumber();
    }

    if (subscription.investmentFund.firstInstalmentPercentage) {
        if (
            latestCapitalAccount &&
            (capitalCallsBetweenSubscriptionSignatureAndCapitalAccount ||
                closingsBetweenSubscriptionSignatureAndCapitalAccount)
        ) {
            // apply formula using capital account
            return sharesNumber
                .times(+latestCapitalAccount.vl)
                .add(capitalCalledAfterLatestCapitalAccount)
                .toNumber();
        } else {
            // no capital account
            return investingSum
                .times(
                    capitalCalls.reduce(
                        (acc, crt) => acc + +crt.percentage,
                        subscription.investmentFund.firstInstalmentPercentage
                    )
                )
                .div(100)
                .toNumber();
        }
    } else if (
        !subscriptionFinishedBeforeLatestCapitalCall &&
        !subscriptionFinishedBeforeLatestClosing
    ) {
        // chronology is:
        // 1 capital call / closing or missing
        // 2 BS signature
        return 0;
    } else if (
        latestCapitalAccount &&
        (capitalCallsBetweenSubscriptionSignatureAndCapitalAccount ||
            closingsBetweenSubscriptionSignatureAndCapitalAccount)
    ) {
        // chronology is:
        // 1 BS signature
        // 2 capital call / closing
        // 3 capital account
        // apply formula
        return sharesNumber
            .times(latestCapitalAccount ? +latestCapitalAccount.vl : 0)
            .add(capitalCalledAfterLatestCapitalAccount)
            .toNumber();
    } else {
        // chronology is:
        // 1 BS signature
        // 2 capital call / closing (no capital account)
        return investingSum
            .times(capitalCalls.reduce((acc, crt) => acc + +crt.percentage, 0))
            .div(100)
            .toNumber();
    }
};

export const getSubscriptionData = ({
    subscription,
    shareSubcategories,
    capitalAccounts,
    capitalCalls,
    closings,
    navs,
    fund,
    subscriptionNAV,
}) => {
    const { shareSubcategory, shareValue } = subscription.investmentCharacteristics;
    const subscategoryId = shareSubcategory?.id || getShareSubcategoryIdFromIRI(shareSubcategory);
    const subcategory = shareSubcategories.find(sc => sc.id === subscategoryId);
    const investingSum = new Big(subscription.investmentCharacteristics.investingSum);
    const sharesNumber = shareValue ? investingSum.div(shareValue) : new Big(0);

    const closingsByFund = closings.filter(
        c => c.investmentFund.id === subscription.investmentFund.id
    );
    const capitalAccountsByFund = capitalAccounts.filter(
        ca =>
            getShareSubcategoryIdFromIRI(ca.shareSubcategory) === subcategory?.id &&
            (ca.status === capitalAccountStatus.ACTIVATED ||
                ca.status === capitalAccountStatus.COMPLETED)
    );
    const capitalCallsByFund = capitalCalls.filter(
        c =>
            getEntityIdFromIRI(c.investmentFund, entityKey.INVESTMENT_FUND) ===
            subscription.investmentFund.id
    );
    const latestClosing = getLatestClosing(closingsByFund);
    const latestCapitalAccount = getLatestCapitalAccount(capitalAccountsByFund);
    const latestCapitalCall = getLatestClosing(capitalCallsByFund);

    const subscriptionFinishedBeforeLatestClosing = latestClosing
        ? moment(subscription.finishedSubscriptionAt).isBefore(
              moment(latestClosing.activatedAt),
              'day'
          )
        : false;
    const subscriptionFinishedBeforeLatestCapitalCall = latestCapitalCall
        ? moment(subscription.finishedSubscriptionAt).isBefore(
              moment(latestCapitalCall.activatedAt),
              'day'
          )
        : false;

    return {
        valuation: getSubscriptionValuation({
            subscription,
            subscriptionNAV,
            latestCapitalAccount,
            subscriptionFinishedBeforeLatestCapitalCall,
            subscriptionFinishedBeforeLatestClosing,
            subcategory,
            capitalCalls: capitalCallsByFund,
            closings: closingsByFund,
            navs,
            fund,
        }),
        sharesNumber: sharesNumber.toNumber(),
        investmentSumCalled: getSubscriptionTotalCapitalCalled({
            subscription,
            capitalCalls: capitalCallsByFund,
            subscriptionFinishedBeforeLatestClosing,
            subscriptionFinishedBeforeLatestCapitalCall,
        }),
        latestLiquidityDate: getSubscriptionLiquidityDate({
            subscription,
            subscriptionNAV,
            latestCapitalAccount,
            subscriptionFinishedBeforeLatestClosing,
            subscriptionFinishedBeforeLatestCapitalCall,
            navs,
            fund,
        }),
        subcategory,
    };
};

export const getSubscriptionNAV = ({ subscription, navs }) =>
    // if shareValue is present it means that the subscription ticks off all the other conditions that are necessary
    subscription.investmentCharacteristics?.shareValue
        ? sortByDate([...navs], 'activatedAt', 'desc').find(
              // we sort them first to find the activated NAV just after the subscription was finished
              nav =>
                  getEntityIdFromIRI(nav.shareSubategory, entityKey.SUBCATEGORY) ===
                      subscription.investmentCharacteristics.shareSubcategory.id &&
                  moment(nav.activatedAt).isSameOrAfter(subscription.finishedSubscriptionAt, 'd')
          )
        : null; // no share value means there's no NAV that fits

export const getFormattedAddress = address => {
    const selectedCountry = countries.find(country => country.id === address.country)?.name || '';
    return `${address.address}, ${address.zipCode ? address.zipCode : ''}${
        address.city ? ` ${address.city}, ` : ''
    }${selectedCountry}`;
};

export const purgeEmptyFields = (obj, purgedValue) => {
    const result = {};
    Object.keys(obj).forEach(key => {
        if (typeof purgedValue !== 'undefined') {
            if (obj[key] !== purgedValue) {
                result[key] = obj[key];
            }
        } else if (obj[key]) {
            result[key] = obj[key];
        }
    });

    return result;
};

export const getButtonLoaderSize = (size = 'large') => {
    switch (size) {
        case 'small':
            return 15;
        case 'medium':
            return 18;
        case 'large':
            return 22;
        default:
            return 20;
    }
};

export const sortStringValueByKey = key => (a, b) => {
    if (a[key].toUpperCase() > b[key].toUpperCase()) return 1;
    if (a[key].toUpperCase() < b[key].toUpperCase()) return -1;
    return 0;
};

export const sortArrayByKey = key => (a, b) => {
    if (a[key] > b[key]) return 1;
    if (a[key] < b[key]) return -1;
    return 0;
};

export function sortByDate(list, attributeKey, order = 'asc') {
    return list.sort((a, b) => {
        const d1 = moment(a[attributeKey]);
        const d2 = moment(b[attributeKey]);

        if (d1.isBefore(d2, 'd')) return order === 'asc' ? -1 : 1;
        if (d1.isAfter(d2, 'd')) return order === 'asc' ? 1 : -1;
        return 0;
    });
}

export const parsePaymentNumber = (type, number, closingComment) => {
    if (type === 'fundraising') {
        return texts.FUND_CALL(number);
    }
    if (type === 'closing') {
        return texts.CLOSING_NUMBER(closingComment);
    }

    return texts.FUND_CALL(number);
};

export const getProfileByRoles = (savedRoles, ppRoles, pmRoles) => {
    if (!savedRoles || !savedRoles.length) return 'all';
    if (savedRoles.every(sr => ppRoles.find(x => x.id === sr))) {
        return 'PP';
    }
    if (savedRoles.every(sr => pmRoles.find(x => x.id === sr))) {
        return 'PM';
    }
    return 'all';
};

export const parseRoleByProfile = (savedRole, allRoles) => {
    switch (savedRole) {
        case 'PP':
            return allRoles.filter(r => r.name.includes('PP')).map(r => r.id);
        case 'PM':
            return allRoles.filter(r => r.name.includes('PM')).map(r => r.id);
        default:
            return allRoles
                .filter(r => r.category?.name === roleCategories.INVESTORS)
                .map(r => r.id);
    }
};

// custom validation Joi schema
export const bicValidationSchema = (value, helpers) => {
    if (value.trim() === '') {
        return helpers.error('string.empty');
    }

    if (value.length !== 8 && value.length !== 11) {
        return helpers.error('string.length');
    }

    if (!/^\w+$/g.test(value)) {
        return helpers.error('string.pattern');
    }

    return value;
};

// Create a lookup map for efficient category finding
export const createCategoryLookup = categories => {
    const lookup = new Map();
    categories.forEach(category => {
        category.shareSubcategories?.forEach(subcategory => {
            lookup.set(subcategory.id, category);
        });
    });
    return lookup;
};

export function getPaymentMethodText(paymentMethod) {
    if (paymentMethod === paymentMethods.CREDIT_TRANSFER) return texts.CREDIT_TRANSFER;
    if (paymentMethod === paymentMethods.DIRECT_DEBIT) return texts.DIRECT_DEBIT;
    return '-';
}

export const sortRoutesByRole = (routes, roleKey) => {
    const orderList = routeMaps[roleKey] || [];
    const orderMap = new Map(orderList.map((key, idx) => [key, idx]));

    return routes.slice().sort((a, b) => {
        const orderA = orderMap.has(a.key) ? orderMap.get(a.key) : Infinity;
        const orderB = orderMap.has(b.key) ? orderMap.get(b.key) : Infinity;
        return orderA - orderB;
    });
};
